import React, { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const MIN_REASON_CHARS = 10;
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '60 minutes' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
];

/**
 * ImpersonateModal
 *
 * Confirms intent before starting an impersonation session. Captures the
 * reason (audit trail) and duration cap, then POSTs to
 * /api/admin/v2/orgs/:orgId/impersonate.
 *
 * Props:
 *   - org:        { id, name }
 *   - onClose:    () => void
 *   - onStarted:  (responseBody) => void  // optional; called on 200
 */
export default function ImpersonateModal({ org, onClose, onStarted }) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const reasonRef = useRef(null);

  useEffect(() => {
    // Focus the reason field on open for keyboard users.
    if (reasonRef.current) reasonRef.current.focus();
  }, []);

  // Escape closes the modal
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  if (!org) return null;

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= MIN_REASON_CHARS;
  const canSubmit = reasonValid && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: trimmed, duration_minutes: Number(duration) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        setError(data.error || data.message || `Server returned ${res.status}`);
        setSubmitting(false);
        return;
      }
      if (onStarted) onStarted(data);
      // Land on the impersonated org's app shell. ProtectedApp will read the
      // session (now flagged with impersonation) and render the dashboard with
      // the persistent red banner pinned at the top.
      window.location.href = '/app';
    } catch (err) {
      setError(err.message || 'Network error starting impersonation.');
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="impersonate-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg bg-slate-900 border border-slate-700/70 rounded-2xl shadow-2xl text-slate-100"
      >
        <div className="px-6 py-5 border-b border-slate-700/60">
          <h3 id="impersonate-modal-title" className="text-lg font-bold tracking-tight">
            View as {org.name}
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            You'll see this organization's data in read-only mode for the selected duration. Every action is logged for SOC 2 audit.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label htmlFor="impersonate-reason" className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Reason for impersonation <span className="text-red-400">*</span>
            </label>
            <textarea
              id="impersonate-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Customer support ticket #1234 · Onboarding walkthrough · Investigating data issue with finance"
              className="w-full rounded-lg bg-slate-800/80 border border-slate-700 px-3 py-2 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60"
              disabled={submitting}
              required
              minLength={MIN_REASON_CHARS}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={reasonValid ? 'text-emerald-400' : 'text-slate-500'}>
                {reasonValid
                  ? 'Reason captured for audit log.'
                  : `Minimum ${MIN_REASON_CHARS} characters (${trimmed.length}/${MIN_REASON_CHARS}).`}
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="impersonate-duration" className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Session duration
            </label>
            <select
              id="impersonate-duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              disabled={submitting}
              className="w-full rounded-lg bg-slate-800/80 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/60"
            >
              {DURATION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              The session ends automatically after the duration expires.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-700/60 flex justify-end gap-2 bg-slate-900/60 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Starting session…' : 'Start session'}
          </button>
        </div>
      </form>
    </div>
  );
}
